import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Alert, Card, Empty, Flex, Result, Skeleton, Space, Spin, Typography } from 'antd';
import { useHref, useLinkClickHandler } from 'react-router-dom';
import { errorMessageFa, isApiError } from '../api/api-error.js';
import { Button, IconButton } from '../antd/button.js';
import { SkeletonBlock, SkeletonGroup, SkeletonLines } from '../motion/presence.js';
import { Numeric } from './bidi.js';
const { Paragraph, Text } = Typography;
/**
 * «کد پیگیری» — the request id a user reads to support, with a copy button.
 *
 * One label everywhere. It used to be «شناسهٔ درخواست» on the page-sized error
 * and «کد پیگیری» on the inline one for the same value. The id is a random
 * Latin string («xLC9l-eBYT8a…») that a manager used to have to spell out over
 * the phone; the button copies it instead. It is an LTR island (`Numeric`), so
 * its hyphens and digits keep their order inside the Persian sentence.
 */
export function TrackingCode({ value }) {
    const [copied, setCopied] = useState(false);
    async function copy() {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2_000);
        }
        catch {
            // No clipboard (an insecure origin, a locked-down browser): the code is
            // still on screen to read out.
        }
    }
    return (_jsxs(Text, { type: "secondary", style: { fontSize: 'var(--bk-text-caption)' }, children: ["\u06A9\u062F \u067E\u06CC\u06AF\u06CC\u0631\u06CC: ", _jsx(Numeric, { className: "bk-mono", children: value }), ' ', _jsx(IconButton, { type: "text", size: "small", name: copied ? 'check' : 'copy', label: "\u06A9\u067E\u06CC \u06A9\u062F \u067E\u06CC\u06AF\u06CC\u0631\u06CC", onClick: () => void copy() }), _jsx("span", { role: "status", className: "bk-visually-hidden", children: copied ? 'کپی شد' : '' })] }));
}
/**
 * A failed query, rendered in place.
 *
 * The server's Persian `message` is the headline because it is written for this
 * exact failure; the panel adds only the `requestId`, which is what turns a
 * user's "it broke" into a line the API's logs can be grepped for.
 */
export function QueryError({ error, onRetry, title, support }) {
    const requestId = isApiError(error) ? error.requestId : undefined;
    const forbidden = isApiError(error) && error.isForbidden;
    return (_jsx(Result, { status: forbidden ? 403 : 'error', title: title ?? (forbidden ? 'دسترسی ندارید' : 'بارگذاری انجام نشد'), subTitle: errorMessageFa(error), extra: _jsxs(Flex, { vertical: true, align: "center", gap: 8, children: [onRetry ? (_jsx(Button, { type: "primary", onClick: onRetry, children: "\u062A\u0644\u0627\u0634 \u062F\u0648\u0628\u0627\u0631\u0647" })) : null, requestId ? _jsx(TrackingCode, { value: requestId }) : null, support] }) }));
}
export function EmptyState({ title, description, action, help, compact = false, }) {
    return (_jsxs(Empty, { image: Empty.PRESENTED_IMAGE_SIMPLE, styles: compact ? { image: { height: 40 } } : undefined, description: _jsxs("div", { children: [_jsx(Paragraph, { strong: !compact, style: { marginBlockEnd: description ? 4 : 0 }, children: title }), description ? _jsx(Text, { type: "secondary", children: description }) : null] }), children: [action, help ? _jsx("div", { className: "bk-empty-state__help", children: help }) : null] }));
}
export function LoadingBlock({ rows = 4, spinner = false, height = 200, title = true, }) {
    if (spinner) {
        return (_jsx(Flex, { align: "center", justify: "center", style: { blockSize: height }, children: _jsx(Spin, {}) }));
    }
    return _jsx(Skeleton, { active: true, title: title, paragraph: { rows } });
}
/**
 * A failed fetch, explained inside the thing that failed.
 *
 * Deliberately NOT `<QueryError>`: that one is a full-page `<Result>`, and
 * dropping a page-sized result block inside a 200px dashboard card is how one
 * failed query takes over the screen. Both panels had grown a local copy of
 * this — the clinic panel's in `components/states.tsx`, the console's inline in
 * three feature files — so it lives here now.
 *
 * `errorMessageFa` returns the server's own Persian sentence when there is one
 * and the shared code table otherwise, so this never shows a raw code to a
 * user. `requestId` appears only when the server sent one: it is what a clinic
 * quotes to support, and it is noise when absent.
 */
export function ErrorState({ error, title = 'بارگذاری نشد', onRetry, compact, support, }) {
    const requestId = isApiError(error) ? error.requestId : undefined;
    return (_jsx(Alert, { type: "error", showIcon: true, title: title, description: _jsxs(Space, { orientation: "vertical", size: 4, children: [_jsx("span", { children: errorMessageFa(error) }), requestId ? _jsx(TrackingCode, { value: requestId }) : null, support] }), style: compact ? { padding: 'var(--bk-space-2) var(--bk-space-3)' } : undefined, action: onRetry ? (_jsx(Button, { size: "small", onClick: onRetry, children: "\u062A\u0644\u0627\u0634 \u062F\u0648\u0628\u0627\u0631\u0647" })) : null }));
}
/* -------------------------------------------------------------------------- */
/* Route-level states                                                         */
/* -------------------------------------------------------------------------- */
/**
 * Fallback for a lazily-loaded route chunk.
 *
 * Deliberately a skeleton shaped like a page — header, then a card — rather
 * than a spinner: on a slow Iranian connection the chunk can take a second, and
 * a layout-shaped placeholder makes the arrival feel like a paint rather than a
 * jump.
 */
export function PageFallback() {
    // Heading and subtitle at `PageHeader`'s sizes, then one card with a short
    // title and a few rows: the silhouette most routes resolve to.
    return (_jsxs(SkeletonGroup, { label: "\u062F\u0631 \u062D\u0627\u0644 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \u0635\u0641\u062D\u0647\u2026", children: [_jsxs(Flex, { vertical: true, gap: "var(--bk-space-2)", style: { marginBlockEnd: 'var(--bk-space-5)' }, children: [_jsx(SkeletonBlock, { shape: "text", width: 220, height: "var(--bk-text-heading)" }), _jsx(SkeletonBlock, { shape: "text", width: "min(420px, 60%)" })] }), _jsx(Card, { children: _jsxs(Flex, { vertical: true, gap: "var(--bk-space-4)", children: [_jsx(SkeletonBlock, { shape: "text", width: "28%", height: "var(--bk-text-title-sm)" }), _jsx(SkeletonLines, { lines: 6, lastLineWidth: "45%" })] }) })] }));
}
/**
 * The «back home» action of the dead-end screens, as ONE control.
 *
 * It used to be `<Link><Button/></Link>`: an interactive element inside
 * another — two tab stops, the outer one nameless, and a doubled focus ring.
 * A `Button` with `href` renders a single `<a class="ant-btn">`; the router's
 * click handler keeps it a client-side navigation while leaving middle-click
 * and ⌘-click to the browser. The same technique as the clinic panel's
 * `LinkButton`.
 */
function HomeButton({ to, children }) {
    const href = useHref(to);
    const handleClick = useLinkClickHandler(to);
    return (_jsx(Button, { type: "primary", href: href, onClick: (event) => handleClick(event), children: children }));
}
/**
 * 403.
 *
 * The subtitle names the person who can fix it, because "you do not have
 * access" with no next step turns into a support ticket that says exactly that
 * and nothing more. Permissions in this product are granted by the clinic
 * owner (`SPEC.md` §2.4), not by us.
 */
export function Forbidden403({ homeHref = '/', homeLabel = 'بازگشت به صفحهٔ اصلی', detail, extra, }) {
    return (_jsx(Result, { status: "403", title: "\u062F\u0633\u062A\u0631\u0633\u06CC \u0646\u062F\u0627\u0631\u06CC\u062F", subTitle: detail ?? 'برای دسترسی به این بخش، با مالک کلینیک تماس بگیرید.', extra: _jsxs(Space, { wrap: true, children: [_jsx(HomeButton, { to: homeHref, children: homeLabel }), extra] }) }));
}
/** 404 — a route that does not exist, usually an old bookmark. */
export function NotFound404({ homeHref = '/', homeLabel = 'بازگشت به صفحهٔ اصلی', detail, extra, }) {
    return (_jsx(Result, { status: "404", title: "\u0635\u0641\u062D\u0647 \u067E\u06CC\u062F\u0627 \u0646\u0634\u062F", subTitle: detail ?? 'این صفحه وجود نداره یا جابه‌جا شده.', extra: _jsxs(Space, { wrap: true, children: [_jsx(HomeButton, { to: homeHref, children: homeLabel }), extra] }) }));
}
//# sourceMappingURL=states.js.map